import React from "react";
import { TitleConfig } from "@/types/TitleConfig";

interface TemplateWrapperProps {
  children: React.ReactNode;
  background: string;
  style?: React.CSSProperties;
}

const TemplateWrapper: React.FC<TemplateWrapperProps> = ({ 
  children, 
  background,
  style
}) => {
  return (
    <div className={`min-h-screen ${background} overflow-x-hidden`} style={{...style, backgroundColor: 'transparent'}}>
      {children}
    </div>
  );
};

export const ClassicTemplate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <TemplateWrapper background="bg-gradient-to-b from-[#8B1F35] to-[#5E1525]">
      {children}
    </TemplateWrapper>
  );
};

export const ModernTemplate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <TemplateWrapper background="bg-gradient-to-r from-[#2C3E50] to-[#3498DB]">
      {children}
    </TemplateWrapper>
  );
};

export const ElegantTemplate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <TemplateWrapper background="bg-gradient-to-br from-[#1A237E] via-[#311B92] to-[#4A148C]">
      {children}
    </TemplateWrapper>
  );
};

export const TraditionalTemplate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <TemplateWrapper background="bg-[#C19B6C] bg-opacity-90">
      {children}
    </TemplateWrapper>
  );
};

export const MinimalistTemplate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <TemplateWrapper background="bg-[#F5F5F5]">
      {children}
    </TemplateWrapper>
  );
};

export const CustomTemplate: React.FC<{ children: React.ReactNode, background?: { type: 'image', image: { url: string } } }> = ({ children, background }) => {
  return (
    <TemplateWrapper 
      background=""
      style={{
        backgroundImage: background?.type === 'image' && background?.image?.url ? `url(${background.image.url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: 'transparent',
      }}
    >
      <div className="min-h-screen flex flex-col">
        {children}
      </div>
    </TemplateWrapper>
  );
};

export const DailyStudyTemplate: React.FC<{ slots: Record<string, React.ReactNode> }> = ({ slots }) => {
  return (
    <div
      style={{
        backgroundImage: `url(/images/The-daily-study%20(2).webp)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        width: '100vw',
        position: 'relative',
        direction: 'rtl',
        overflow: 'hidden',
        backgroundColor: 'transparent',
      }}
    >
      {/* Top bar */}
      <div className="absolute top-0 right-0 left-0 h-48 sm:h-56 md:h-[240px] flex items-center justify-center z-10 bg-transparent">
        {slots.topBar}
      </div>
      {/* Time center */}
      <div className="absolute top-48 sm:top-56 md:top-[240px] right-0 left-0 h-64 sm:h-72 md:h-[320px] flex items-center justify-center z-10 bg-transparent">
        {slots.time}
      </div>
      {/* Main title */}
      <div className="absolute top-112 sm:top-128 md:top-[560px] right-0 left-0 h-64 sm:h-72 md:h-[320px] flex items-center justify-center z-10 bg-transparent">
        {slots.title}
      </div>
      {/* Study blocks grid */}
      <div className="absolute top-176 sm:top-200 md:top-[880px] right-0 left-0 h-[1120px] sm:h-[1200px] md:h-[1280px] flex flex-wrap justify-center items-start gap-6 sm:gap-8 md:gap-12 z-10 bg-transparent px-4 sm:px-8 md:px-12">
        {slots.studyBlocks}
      </div>
      {/* Bottom bar */}
      <div className="absolute bottom-16 sm:bottom-24 md:bottom-40 right-0 left-0 h-48 sm:h-56 md:h-[240px] flex items-center justify-center z-10 bg-transparent">
        {slots.bottomBar}
      </div>
    </div>
  );
};

interface HomeTemplateProps {
  children: React.ReactNode;
  titleConfig?: TitleConfig;
}

export const HomeTemplate: React.FC<HomeTemplateProps> = ({ children, titleConfig }) => {
  const getTemplateClass = () => {
    switch (titleConfig?.template) {
      case 'classic':
        return 'bg-gradient-to-r from-blue-600 to-blue-800';
      case 'modern':
        return 'bg-gradient-to-r from-purple-600 to-indigo-600';
      case 'elegant':
        return 'bg-gradient-to-r from-gray-800 to-gray-900';
      case 'traditional':
        return 'bg-gradient-to-r from-amber-700 to-amber-900';
      case 'minimalist':
        return 'bg-[#F5F5F5]';
      case 'custom':
        return titleConfig.background?.type === 'image' && titleConfig.background?.image?.url
          ? 'bg-cover bg-center bg-no-repeat'
          : 'bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200';
      default:
        return 'bg-gradient-to-r from-blue-600 to-blue-800';
    }
  };

  return (
    <TemplateWrapper
      background={getTemplateClass()}
      style={
        titleConfig?.background?.type === 'image' && titleConfig?.background?.image?.url
          ? {
              backgroundImage: `url(${titleConfig.background.image.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              position: 'relative',
            }
          : undefined
      }
    >
      {titleConfig?.background?.type === 'image' && titleConfig?.background?.image?.url && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: titleConfig.background.image.overlayColor || 'rgba(0,0,0,0.5)',
            backdropFilter: `blur(${titleConfig.background.image.blur || 0}px)`,
            opacity: titleConfig.background.image.opacity || 0.5,
            zIndex: 0,
          }}
        />
      )}
      <div className="min-h-screen flex flex-col relative z-10">
        {children}
      </div>
    </TemplateWrapper>
  );
};
